import {
  BaseQuestionResource,
  QuestionType,
} from "./types/BaseQuestionResource";
import { Risk, RiskType, Status } from "../singleCase/types/Case";
import {
  isBaseQuestionAnswered,
  questionHasRemainingDisclosures,
} from "./validation/baseQuestion";

import { CurrentPageAndSection } from "./types/CurrentPageAndSection";
import { CustomSections } from "../../features/interview/types";
import { EdhConditionDisclosureResource } from "./types/EdhConditionDisclosureResource";
import { EdhQuestionPagesResource } from "./types/EdhQuestionPagesResource";
import { PhysicianResource } from "../physician/types/PhysicianResource";
import { QuestionPageResource } from "./types/QuestionPageResource";
import { QuestionSectionResource } from "./types/QuestionSectionResource";
import { findLastActivePageAndSection } from "./updateUtils/updateCurrent";
import { pageHasVisibleQuestions } from "./validation/page";
import { sectionHasVisibleQuestions } from "./validation/section";

/**
 * Check if interview is active.
 */

export const interviewIsActive = (status: Status) => {
  if (
    status === Status.CUSTOMER_INITIALIZED ||
    status === Status.SIGN_ORDER_CREATED
  ) {
    return true;
  }
  return false;
};

/**
 * Gets the total count of pages with visible questions.
 * @param pages List of pages.
 */
export const getVisiblePagesCount = (pages: QuestionPageResource[]) => {
  return pages.filter((page) => pageHasVisibleQuestions(page)).length;
};

/**
 * Gets the total count of sections with visible questions.
 * @param pages List of pages.
 */
export const getVisibleSectionsCount = (pages: QuestionPageResource[]) => {
  return pages.reduce((acc, { sections }) => {
    return (
      acc +
      sections.reduce((sectionCount, section) => {
        if (sectionHasVisibleQuestions(section)) {
          return sectionCount + 1;
        }
        return sectionCount;
      }, 0)
    );
  }, 0);
};

/**
 * Gets the total count of questions in a list of pages.
 * @param pages List of pages.
 */
export const getTotalQuestionsCount = (pages: QuestionPageResource[]) => {
  return pages
    .map(({ code, sections }) => {
      return {
        code,
        questionCount: sections
          .filter(
            (section) =>
              sectionHasVisibleQuestions(section) ||
              section.code === CustomSections.PRIMARY_PHYSICIAN,
          )
          .map((section: QuestionSectionResource) => {
            // Need to count primary physician section.
            if (section.code === CustomSections.PRIMARY_PHYSICIAN) {
              return {
                code: section.code,
                questionCount: 1,
              };
            }

            return {
              code: section.code,
              questionCount: section.baseQuestions.reduce(
                (acc, baseQuestion: BaseQuestionResource) => {
                  if (
                    baseQuestion.visible &&
                    baseQuestion.questionType !== QuestionType.HEADING
                  ) {
                    return acc + 1;
                  }
                  return acc;
                },
                0,
              ),
            };
          })
          .reduce((acc, section) => {
            if (section) {
              return acc + section.questionCount;
            }
            return acc;
          }, 0),
      };
    })
    .reduce((acc, section) => {
      return acc + section.questionCount;
    }, 0);
};

/**
 * Gets the index of a section in a flattened ssection list.
 * @param sectionCode A valid section code.
 * @param pages List of interview pages.
 */
export const getOrderedSectionIndex = (
  sectionCode: string,
  pages: QuestionPageResource[],
) => {
  const sectionList = pages.map((page) => {
    return [
      ...page.sections.filter((section) => sectionHasVisibleQuestions(section)),
    ];
  });
  const orderedList = ([] as QuestionSectionResource[]).concat(...sectionList);
  return orderedList.findIndex((section) => section.code === sectionCode);
};

/**
 * Gets the last page containing unanswered questions.
 * @param pages List of pages.
 */
export const getLastPageWithUnansweredQuestions = (
  pages: QuestionPageResource[],
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  return pages.find(({ sections }) =>
    sections.find(({ baseQuestions }) =>
      baseQuestions.find(
        (baseQuestion) =>
          baseQuestion.visible &&
          (!isBaseQuestionAnswered(baseQuestion) ||
            questionHasRemainingDisclosures(baseQuestion, embeddedDisclosures)),
      ),
    ),
  );
};

/**
 * Gets the last section containing unanswered questions.
 * @param sections List of sections.
 */
export const getLastSectionWithUnansweredQuestions = (
  sections: QuestionSectionResource[],
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  return sections.find(({ baseQuestions }) =>
    baseQuestions.find(
      (baseQuestion) =>
        !isBaseQuestionAnswered(baseQuestion) ||
        questionHasRemainingDisclosures(baseQuestion, embeddedDisclosures),
    ),
  );
};

/**
 * Gets the index of a page or section from a provided code.
 * @param array Array of pages or sections.
 * @param searchCode The section or page code to search for.
 */
export const getIndexByCode = (
  array: QuestionPageResource[] | QuestionSectionResource[],
  searchCode: string,
) => {
  return array.findIndex(
    ({ code }: QuestionPageResource | QuestionSectionResource) =>
      code === searchCode,
  );
};

/**
 * Get index of next section with unanswered questions.
 * @param pages A list of interview pages.
 * @param code A page code.
 */
export const getNextSectionIndex = (
  pages: QuestionPageResource[],
  code: string,
) => {
  return pages
    .find((interviewPage) => interviewPage.code === code)
    ?.sections.findIndex(
      (section) =>
        section.baseQuestions.filter(
          (baseQuestion) => !isBaseQuestionAnswered(baseQuestion),
        ).length > 0,
    );
};

/**
 * Gets a list of sections from a page based on the page code.
 * @param pages List of pages.
 * @param pageCode Code of the page you want to get the sections of.
 */
export const getSectionsByPageCode = (
  pages: QuestionPageResource[],
  pageCode: string,
) => {
  return pages.find((page) => page.code === pageCode)?.sections;
};

/**
 * Gets the total amount of unanswered questions left in the interview.
 * @param pages List of interview pages.
 * @param physicians The list of physicians.
 * @param embeddedDisclosures The list of embedded disclosures.
 */
export const getUnansweredQuestionsCount = (
  pages: QuestionPageResource[],
  physicians: any,
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  return pages
    .map(({ code, sections }) => {
      return {
        code,
        remainingQuestions: sections
          .filter(
            (section: QuestionSectionResource) =>
              sectionHasVisibleQuestions(section) ||
              section.code === CustomSections.PRIMARY_PHYSICIAN,
          )
          .map((section: QuestionSectionResource) => {
            /* Special handling for primary physician section.
               If set will return 0 remaining, else return 1. */
            if (section.code === CustomSections.PRIMARY_PHYSICIAN) {
              return {
                code: section.code,
                remainingQuestions: physicians && physicians[0] ? 0 : 1,
              };
            }

            return {
              code: section.code,
              remainingQuestions: section.baseQuestions.reduce(
                (acc, baseQuestion: BaseQuestionResource) => {
                  if (
                    baseQuestion.visible &&
                    baseQuestion.questionType !== QuestionType.HEADING &&
                    (!isBaseQuestionAnswered(baseQuestion) ||
                      questionHasRemainingDisclosures(
                        baseQuestion,
                        embeddedDisclosures,
                      ))
                  ) {
                    return acc + 1;
                  }
                  return acc;
                },
                0,
              ),
            };
          })
          .reduce((sectionCount, section) => {
            if (section) {
              return sectionCount + section.remainingQuestions;
            }
            return sectionCount;
          }, 0),
      };
    })
    .reduce((acc, section) => {
      return acc + section.remainingQuestions;
    }, 0);
};

/**
 * Gets the total amount of answered questions left in the interview.
 * @param pages List of interview pages.
 * @param physicians The list of physicians.
 */
export const getAnsweredQuestionsCount = (
  pages: QuestionPageResource[],
  physicians: any,
) => {
  return pages
    .map(({ code, sections }) => {
      return {
        code,
        answeredQuestions: sections
          .filter(
            (section: QuestionSectionResource) =>
              sectionHasVisibleQuestions(section) ||
              section.code === CustomSections.PRIMARY_PHYSICIAN,
          )
          .map((section: QuestionSectionResource) => {
            /*
             * Handles primary physician check. If set will return 0 remaining, else return 1.
             */
            if (
              physicians &&
              section.code === CustomSections.PRIMARY_PHYSICIAN
            ) {
              return {
                code: section.code,
                answeredQuestions: physicians[0] ? 1 : 0,
              };
            }

            return {
              code: section.code,
              answeredQuestions: section.baseQuestions.reduce(
                (acc, baseQuestion: BaseQuestionResource) => {
                  if (
                    baseQuestion.visible &&
                    baseQuestion.questionType !== QuestionType.HEADING &&
                    isBaseQuestionAnswered(baseQuestion)
                  ) {
                    return acc + 1;
                  }
                  return acc;
                },
                0,
              ),
            };
          })
          .reduce((acc, section) => {
            if (section) {
              return acc + section.answeredQuestions;
            }
            return acc;
          }, 0),
      };
    })
    .reduce((acc, section) => {
      return acc + section.answeredQuestions;
    }, 0);
};

/**
 * Get index of next unanswered question.
 * @param baseQuestions List of basequestions.
 * @param disclosures List of embedded disclosures.
 */
const getNextUnansweredQuestionIndex = (
  baseQuestions: BaseQuestionResource[],
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  return baseQuestions.findIndex(
    (baseQuestion) =>
      (baseQuestion.questionType !== QuestionType.HEADING &&
        baseQuestion.visible &&
        !isBaseQuestionAnswered(baseQuestion)) ||
      questionHasRemainingDisclosures(baseQuestion, embeddedDisclosures),
  );
};

/**
 * Get the list of base questions to be displayed in the interview.
 * When given a list, it will return any previously answered questions and
 * the next unanswered question.
 *
 * @param baseQuestions A list of base questions.
 * @param disclosures The list of embedded disclosures.
 */
export const filterBaseQuestions = (
  baseQuestions: BaseQuestionResource[],
  embeddedDisclosures?: EdhConditionDisclosureResource[],
) => {
  // Find index of next visible and unanswered question.
  const nextUnansweredQuestionIndex = getNextUnansweredQuestionIndex(
    baseQuestions,
    embeddedDisclosures,
  );

  return baseQuestions.reduce((filteredQuestions, baseQuestion, index) => {
    if (nextUnansweredQuestionIndex === -1) {
      filteredQuestions.push(baseQuestion);
    }

    if (index < nextUnansweredQuestionIndex) {
      filteredQuestions.push(baseQuestion);
    }

    if (index === nextUnansweredQuestionIndex) {
      filteredQuestions.push(baseQuestion);
    }
    return filteredQuestions;
  }, [] as BaseQuestionResource[]);
};

// Get all answered sections + next section with unanswered questions.
export const filterSections = (
  embeddedDisclosures: EdhConditionDisclosureResource[],
  nextSectionIndex: number,
  sections: QuestionSectionResource[],
) => {
  return sections.reduce((filteredSections, section, index) => {
    const { baseQuestions } = section;

    if (nextSectionIndex !== undefined && sectionHasVisibleQuestions(section)) {
      if (index < nextSectionIndex || nextSectionIndex === -1) {
        filteredSections.push(section);
      }
      if (index === nextSectionIndex) {
        filteredSections.push({
          ...section,
          baseQuestions: filterBaseQuestions(
            baseQuestions,
            embeddedDisclosures,
          ),
        });
      }
    }
    return filteredSections;
  }, [] as QuestionSectionResource[]);
};

/** URLs for disclosures containing slashes or dots will not work with the backend.
 *  Use this to convert the URL to one compatible with the backend.
 *
 * @param url The url from a disclosure/question.
 */
export const replaceUrlSlashAndDot = (url: string) => {
  let sanitizedUrl = url;

  if (sanitizedUrl.includes("/")) {
    sanitizedUrl = sanitizedUrl.replace(/\//g, "*slash*");
  }

  if (url.includes(".")) {
    sanitizedUrl = sanitizedUrl.replace(/\./g, "*dot*");
  }

  return sanitizedUrl;
};

/**
 * Check if user has started answering the interview.
 * @param pages List of interview pages.
 * @param physicians List of physicians.
 */
export const isInterviewTouched = (
  pages: QuestionPageResource[],
  physicians?: PhysicianResource[],
) => {
  return getAnsweredQuestionsCount(pages, physicians) > 0;
};

/** Custom sections are used to enable injection of sections not provided by the backend.
 *  Only relevant values in object are "code" and "text".
 *
 *  code: Used to identify section in Page component.
 *  text: Defines which text is shown in stepper.
 */
export const createPrimaryPhysicianSection = (risks?: Risk[]) => {
  const isChildRisk =
    risks &&
    risks[0] &&
    (risks[0].type === RiskType.CHILD_COLLECTIVE ||
      risks[0].type === RiskType.CHILD_PRIVATE);

  return {
    _embedded: {},
    _links: [],
    _ureMetadata: {
      underwrite: false,
    },
    baseQuestions: [],
    code: CustomSections.PRIMARY_PHYSICIAN,
    isImplicit: false,
    properties: {},
    text: isChildRisk ? "Barnets lege" : "Lege",
  };
};

export const welcomeSection = {
  _embedded: {},
  _links: [],
  _ureMetadata: {
    underwrite: false,
  },
  baseQuestions: [],
  code: CustomSections.WELCOME_MESSAGE,
  isImplicit: false,
  properties: {},
  text: "",
};

/**
 * Merge custom sections into interview structure.
 * @param pages List of interview pages
 */
export const appendCustomSections = (
  pages: QuestionPageResource[],
  risks: Risk[],
) => {
  return pages.map((page, index) => {
    // Append the primary physician section to last page, as the next-to-last section.
    if (index === pages.length - 1) {
      const sections = [...page.sections];

      const primaryPhysicianSection = createPrimaryPhysicianSection(risks);

      sections.splice(page.sections.length - 1, 0, primaryPhysicianSection);

      return {
        ...page,
        sections: sections,
      };
    } else {
      return page;
    }
  });
};

export const initializeInterviewPosition = (
  questions: EdhQuestionPagesResource,
  currentPageAndSection: CurrentPageAndSection,
) => {
  let current = currentPageAndSection;

  const isTouched = isInterviewTouched(
    questions.pages,
    questions._embedded.physicians,
  );

  if (!isTouched) {
    current.section.code = CustomSections.WELCOME_MESSAGE;
  } else {
    current = findLastActivePageAndSection(
      questions.pages,
      currentPageAndSection,
      questions._embedded.disclosures,
    );
  }

  return current;
};

export const getRemainingTimeForCustomer = (
  upperTimeBoundary: number,
  pages: QuestionPageResource[],
  disclosures: EdhConditionDisclosureResource[] | undefined,
  physicians: any,
) => {
  const totalCount = getTotalQuestionsCount(pages);
  const answeredCount = getAnsweredQuestionsCount(pages, physicians);

  const percentageComplete = Math.floor((answeredCount * 100) / totalCount);
  return Math.round(
    upperTimeBoundary - (percentageComplete / 100) * upperTimeBoundary,
  );
};
